/** @format */

import { type UseQueryResult, useQuery } from "@tanstack/react-query";

import { useAccessibleSanctioningBodies } from "@/hooks/useStableFeatureFlags";
import galaxieClient from "./galaxieClient";

export interface SeasonLabel {
  label: string;
  abbreviation: string;
}

export interface SanctioningBodyEntry {
  displayName: string;
  seriesIds: string[];
}

export type SanctioningBodySlug = "nascar" | "arca" | "grcup";

export interface AppConfig {
  hydrateSeries: string;
  version: string;
  deployId: string;
  environment: string;
  isLocal: boolean;
  websocketsEnabled: boolean;
  timezone: string;
  availableSeries: string[];
  availableYears: Record<string, number[]>;
  seasonLabels: Record<string, SeasonLabel>;
  sanctioningBodies: Partial<Record<SanctioningBodySlug, SanctioningBodyEntry>>;
  /** Per-series feature list keyed by `nascar_id` (string). Lets pages
   *  without a `runData` in scope still gate UI through `hasFeature(...)`. */
  seriesFeatures: Record<string, string[]>;
}

// Fallback series lookup for when config hasn't loaded yet
// This matches the values in web/src/utils/utility.ts seriesLookup
const FALLBACK_SERIES_NAMES: Record<string, string> = {
  "1": "NASCAR Cup Series",
  "2": "NASCAR Xfinity Series",
  "3": "NASCAR Craftsman Truck Series",
  "9999": "Toyota GR Cup",
};

const FALLBACK_ABBREVIATIONS: Record<string, string> = {
  "1": "NCS",
  "2": "NXS",
  "3": "NCTS",
  "9999": "GRC",
};

/**
 * Generate placeholder season labels for current year using fallback values.
 * This provides immediate data while the real config loads.
 */
const generatePlaceholderSeasonLabels = (): Record<string, SeasonLabel> => {
  const currentYear = new Date().getFullYear();
  const labels: Record<string, SeasonLabel> = {};

  // Generate labels for current year and a few years back
  for (const [seriesId, name] of Object.entries(FALLBACK_SERIES_NAMES)) {
    for (let year = currentYear - 2; year <= currentYear + 1; year++) {
      labels[`${seriesId}-${year}`] = {
        label: name,
        abbreviation: FALLBACK_ABBREVIATIONS[seriesId] ?? `S${seriesId}`,
      };
    }
  }

  return labels;
};

/**
 * Placeholder config data shown while the real config is loading.
 * Uses fallback values so UI doesn't flash or show undefined.
 */
const PLACEHOLDER_CONFIG: AppConfig = {
  hydrateSeries: "1",
  version: "",
  deployId: "",
  environment: "",
  isLocal: false,
  websocketsEnabled: false,
  timezone: "America/New_York",
  availableSeries: ["1", "2", "3"],
  availableYears: {
    "1": [new Date().getFullYear()],
    "2": [new Date().getFullYear()],
    "3": [new Date().getFullYear()],
  },
  seasonLabels: generatePlaceholderSeasonLabels(),
  sanctioningBodies: {
    nascar: { displayName: "NASCAR", seriesIds: ["1", "2", "3"] },
  },
  // Empty placeholder — until /config/ resolves, page-level feature gates
  // see no features and therefore short-circuit to the universal/no-feature
  // branch. The `useSeriesFeatures` hook returns `undefined` for missing
  // keys, which `hasFeature` treats as "no features supported".
  seriesFeatures: {},
};

/** Short display labels for sanctioning bodies — used as a final fallback
 *  in nav rendering when the live config hasn't surfaced yet. Backend's
 *  SanctioningBody.choices are the source of truth; keep these in sync. */
export const SANCTIONING_BODY_DEFAULT_LABEL: Record<SanctioningBodySlug, string> = {
  nascar: "NASCAR",
  arca: "ARCA",
  grcup: "GR Cup",
};

/**
 * Hook to fetch application configuration from the backend.
 * Configuration is cached for 5 minutes (matching backend cache).
 * Uses placeholderData to show fallback values immediately while loading.
 */
export const useConfig = (): UseQueryResult<AppConfig, Error> => {
  return useQuery<AppConfig, Error>({
    queryKey: ["config"],
    queryFn: async () => {
      const response = await galaxieClient.get("/config/");
      return response.data as AppConfig;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - matches backend cache
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    placeholderData: PLACEHOLDER_CONFIG,
  });
};

/**
 * Convenience hook to get just the hydrate series value.
 */
export const useHydrateSeries = (): string | undefined => {
  const { data } = useConfig();
  return data?.hydrateSeries;
};

/**
 * Convenience hook to get runtime local environment detection.
 * Note: This is separate from build-time VITE_LOCAL variable.
 */
export const useIsLocalRuntime = (): boolean | undefined => {
  const { data } = useConfig();
  return data?.isLocal;
};

/**
 * Convenience hook to get server-side version.
 * Note: This is separate from build-time VITE_VERSION variable.
 */
export const useServerVersion = (): string | undefined => {
  const { data } = useConfig();
  return data?.version;
};

/**
 * Convenience hook to get runtime environment string.
 */
export const useAppEnvironment = (): string | undefined => {
  const { data } = useConfig();
  return data?.environment;
};

/**
 * Convenience hook to get timezone.
 */
export const useAppTimezone = (): string | undefined => {
  const { data } = useConfig();
  return data?.timezone;
};

/**
 * Convenience hook to get list of available series.
 */
export const useAvailableSeries = (): string[] | undefined => {
  const { data } = useConfig();
  return data?.availableSeries;
};

/**
 * Convenience hook to get available years for a specific series.
 * Returns sorted array (newest first) or empty array if series not found.
 *
 * @param seriesID - Optional series ID to filter years by. If not provided, returns empty array.
 * @returns Array of available years for the series, sorted descending
 */
export const useAvailableYears = (seriesID?: string): number[] => {
  const { data } = useConfig();
  if (!data?.availableYears || !seriesID) {
    return [];
  }
  return data.availableYears[seriesID] ?? [];
};

/**
 * Get the full display label for a series in a specific year.
 * Uses placeholderData so always returns a value immediately.
 *
 * @param seriesId - Series ID (numeric or string)
 * @param year - Optional year for historical names (e.g., "Winston Cup Series" for 1995)
 * @returns Display name for the series/year combination
 */
export const useSeriesLabel = (
  seriesId: string | number,
  year?: string | number
): string => {
  const { data } = useConfig();

  if (year && data?.seasonLabels) {
    const key = `${seriesId}-${year}`;
    const entry = data.seasonLabels[key];
    if (entry?.label && entry.label !== "") {
      return entry.label;
    }
  }

  // Fallback for years not in seasonLabels (historical years before hydration)
  return FALLBACK_SERIES_NAMES[String(seriesId)] ?? `Series ${seriesId}`;
};

/**
 * Get the abbreviated name for a series in a specific year.
 * Uses placeholderData so always returns a value immediately.
 *
 * @param seriesId - Series ID (numeric or string)
 * @param year - Optional year for historical abbreviations
 * @returns Abbreviated name for the series/year combination
 */
export const useSeriesAbbreviation = (
  seriesId: string | number,
  year?: string | number
): string => {
  const { data } = useConfig();

  if (year && data?.seasonLabels) {
    const key = `${seriesId}-${year}`;
    const entry = data.seasonLabels[key];
    if (entry?.abbreviation && entry.abbreviation !== "") {
      return entry.abbreviation;
    }
  }

  // Fallback for years not in seasonLabels (historical years before hydration)
  return FALLBACK_ABBREVIATIONS[String(seriesId)] ?? `S${seriesId}`;
};

/**
 * Get current series name (when year not available).
 * Uses current year for the lookup.
 *
 * @param seriesId - Series ID (numeric or string)
 * @returns Current display name for the series
 */
export const useCurrentSeriesName = (seriesId: string | number): string => {
  return useSeriesLabel(seriesId, new Date().getFullYear());
};

/**
 * Hook returning the sanctioning-body grouping from /config/.
 * Each entry maps a body slug ('nascar' | 'arca' | 'grcup') to its display
 * name and the list of nascar_ids that belong to it. Used by the per-body
 * home pages and the top-bar nav cluster.
 */
export const useSanctioningBodies = (): Partial<
  Record<SanctioningBodySlug, SanctioningBodyEntry>
> => {
  const { data } = useConfig();
  return data?.sanctioningBodies ?? PLACEHOLDER_CONFIG.sanctioningBodies;
};

/**
 * Sanctioning bodies the current user should actually see in the nav: the
 * intersection of the PostHog `accessible-sanctioning-bodies` cohort grant
 * and the bodies the deployment has enabled (driven by GALAXIE_HYDRATE_SERIES,
 * which the backend reflects in `/config/`'s `sanctioningBodies`). Preserves
 * the canonical ordering from `useAccessibleSanctioningBodies`.
 */
export const useVisibleSanctioningBodies = (): SanctioningBodySlug[] => {
  const accessible = useAccessibleSanctioningBodies();
  const bodies = useSanctioningBodies();
  return accessible.filter(body => bodies[body]);
};

/**
 * Per-series feature list. Pages that don't have a `runData` (Standings,
 * Schedule, PreRace, etc.) call this with the route's `seriesId` and pipe
 * the result through `hasFeature(...)` for UI gating.
 *
 * Returns `undefined` when the series isn't in the config — callers should
 * treat that as "no features known" and skip rendering anything that
 * depends on features (the safe default in `hasFeature`).
 */
export const useSeriesFeatures = (
  seriesId: string | number | null | undefined
): string[] | undefined => {
  const { data } = useConfig();
  if (seriesId === null || seriesId === undefined) return undefined;
  return data?.seriesFeatures?.[String(seriesId)];
};
